import { useEffect } from 'react';
import type { ViewStyle } from 'react-native';
import {
  type AnimatedStyle,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import { DURATION, SPRING } from '@/constants/animation';

type EntranceType = 'fade' | 'fadeUp' | 'scale';

interface UseEntranceAnimationOptions {
  /** The type of entrance animation. Default: `'fade'`. */
  type?: EntranceType;
  /** Delay before the animation starts (ms). Default: `0`. */
  delay?: number;
  /** Animation duration (ms). Default: `DURATION.slow` (400ms). */
  duration?: number;
  /** Vertical offset for `fadeUp` (px). Default: `16`. */
  distance?: number;
  /** Initial scale for `scale` type. Default: `0.8`. */
  initialScale?: number;
}

/** Returns an animated style for a mount entrance (fade, fade-up, or scale). */
export function useEntranceAnimation({
  type = 'fade',
  delay = 0,
  duration = DURATION.slow,
  distance = 16,
  initialScale = 0.8,
}: UseEntranceAnimationOptions = {}): AnimatedStyle<ViewStyle> {
  const opacity = useSharedValue(0);

  const translateY = useSharedValue(type === 'fadeUp' ? distance : 0);

  const scale = useSharedValue(type === 'scale' ? initialScale : 1);

  const reducedMotion = useReducedMotion();

  useEffect(() => {
    // Honor reduced-motion: snap to the resting state, no fade or travel.
    if (reducedMotion) {
      opacity.value = 1;

      translateY.value = 0;

      scale.value = 1;

      return;
    }

    const timing = { duration };

    opacity.value =
      type === 'scale'
        ? withDelay(delay, withSpring(1, SPRING.gentle))
        : withDelay(delay, withTiming(1, timing));

    if (type === 'fadeUp') {
      translateY.value = withDelay(delay, withTiming(0, timing));
    }

    if (type === 'scale') {
      scale.value = withDelay(delay, withSpring(1, SPRING.bouncy));
    }
  }, []);

  return useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }, { scale: scale.value }],
  }));
}
